import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AutoAwesome,
  ExpandMore,
  ConfirmationNumber as TicketIcon,
  NoteAdd,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const TicketList = ({ tickets, error, hideHeader = false, onCreateAC }) => {
  const { t } = useTranslation();

  // Helper function to collect all ticket IDs with children
  const getAllExpandableTicketIds = (ticketList) => {
    const ids = [];
    const traverse = (ticket) => {
      if (ticket.children && ticket.children.length > 0) {
        ids.push(ticket.id);
        ticket.children.forEach(traverse);
      }
    };
    ticketList?.forEach(traverse);
    return ids;
  };

  const [expanded, setExpanded] = useState(() => new Set(getAllExpandableTicketIds(tickets)));

  const toggleExpand = (ticketId) => {
    setExpanded((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ticketId)) {
        newSet.delete(ticketId);
      } else {
        newSet.add(ticketId);
      }
      return newSet;
    });
  };

  const getPriorityColor = (priority) => {
    const pName = typeof priority === 'object' ? priority.name : priority;
    switch (pName?.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  if (error) {
    return (
      <Box>
        {!hideHeader && (
          <Box className="agent-section-header">
            <Box className="agent-section-header-content">
              <AutoAwesome className="agent-section-header-icon" />
              <Typography variant="h6" className="agent-section-header-title">
                {t('agent.tickets.title')}
              </Typography>
            </Box>
          </Box>
        )}
        <Alert severity="error" className="ticket-list-error-alert">
          {error}
        </Alert>
      </Box>
    );
  }

  if (!tickets || tickets.length === 0) {
    return (
      <Box>
        {!hideHeader && (
          <Box className="agent-section-header">
            <Box className="agent-section-header-content">
              <AutoAwesome className="agent-section-header-icon" />
              <Typography variant="h6" className="agent-section-header-title">
                {t('agent.tickets.title')}
              </Typography>
            </Box>
          </Box>
        )}
        <Paper elevation={2} className="ticket-list-empty-paper">
          <Typography variant="body1" color="text.secondary" align="center">
            {t('agent.tickets.noTickets')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const renderTicketItem = (ticket, level = 0) => {
    const hasChildren = ticket.children && ticket.children.length > 0;
    const isExpanded = expanded.has(ticket.id);
    const ticketType = ticket.type?.toLowerCase() || 'task';
    const ticketTypeClass = ticketType === 'epic' ? 'epic'
      : ticketType === 'feature' ? 'feature'
        : ticketType === 'subtask' ? 'subtask'
          : ticketType === 'task' ? 'task'
            : 'task';
    const paddingLeft = level * 16;

    return (
      <Box key={ticket.id} className="ticket-item-container">
        <Paper
          elevation={level === 0 ? 2 : 1}
          className={`ticket-item-paper level-${level} ${ticketTypeClass}`}
          data-level={level}
          data-type={ticketTypeClass}
          style={{ marginLeft: `${paddingLeft}px` }}
        >
          <Box
            className={`ticket-item-content ${hasChildren ? 'ticket-item-clickable' : ''}`}
            onClick={() => hasChildren && toggleExpand(ticket.id)}
          >
            <Box className="ticket-item-left">
              {hasChildren && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(ticket.id);
                  }}
                  className="ticket-expand-button"
                >
                  <ExpandMore className={`ticket-expand-icon ${isExpanded ? 'expanded' : ''}`} />
                </IconButton>
              )}
              {!hasChildren && <Box className="ticket-icon-spacer" />}

              <TicketIcon className="ticket-icon" />

              <Box className="ticket-number-container">
                <Typography 
                  variant="body2" 
                  className="ticket-number-text"
                  sx={{ 
                    cursor: 'pointer', 
                    color: 'primary.main',
                    '&:hover': { textDecoration: 'underline' } 
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/task/${encodeURIComponent(ticket.ticketNumber)}`, '_blank');
                  }}
                >
                  {ticket.ticketNumber}
                </Typography>
              </Box>
              <Box className="ticket-title-container">
                <Typography
                  variant="body2"
                  className="ticket-title-text level-nested"
                >
                  {ticket.title}
                </Typography>
              </Box>
            </Box>
            <Box className="ticket-item-right">
              {!hideHeader && (
                <Box className="ticket-ac-action-wrapper">
                  {onCreateAC && (ticketTypeClass === 'task' || ticketTypeClass === 'subtask') ? (
                    <Tooltip title={t('agent.tickets.createAC')} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCreateAC(ticket.id, ticket.ticketNumber, ticket.title);
                        }}
                        className="ticket-ac-action-button"
                      >
                        <NoteAdd className="ticket-ac-action-icon" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" className="ticket-ac-action-empty">
                      -
                    </Typography>
                  )}
                </Box>
              )}
              <Box className="ticket-type-chip-wrapper">
                <Chip
                  label={ticket.type || 'Task'}
                  size="small"
                  className={`ticket-type-chip ticket-type-chip-${ticketTypeClass}`}
                />
              </Box>
              <Box className="ticket-priority-chip-wrapper">
                <Chip
                  label={typeof ticket.priority === 'object' ? ticket.priority.name : (ticket.priority || 'Medium')}
                  size="small"
                  color={typeof ticket.priority === 'object' && ticket.priority.colour ? undefined : getPriorityColor(ticket.priority)}
                  sx={typeof ticket.priority === 'object' && ticket.priority.colour ? { borderColor: ticket.priority.colour, color: ticket.priority.colour } : undefined}
                  variant="outlined"
                  className="ticket-priority-chip"
                />
              </Box>
            </Box>
          </Box>
        </Paper>
        {hasChildren && isExpanded && (
          <Box className="ticket-children-container">
            {ticket.children.map((child) => renderTicketItem(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {!hideHeader && (
        <Box className="agent-section-header">
          <Box className="agent-section-header-content">
            <AutoAwesome className="agent-section-header-icon" />
            <Typography variant="h6" className="agent-section-header-title">
              {t('agent.tickets.title')}
            </Typography>
          </Box>
        </Box>
      )}

      <Paper elevation={0} className="ticket-list-container">
        <Box className="ticket-list-header">
          <Box className="ticket-list-header-left">
            <Box className="ticket-header-spacer" />
            <Typography variant="caption" className="ticket-header-label ticket-number-label">
              {t('agent.tickets.ticketNumber')}
            </Typography>
            <Typography variant="caption" className="ticket-header-label ticket-title-label">
              {t('agent.tickets.ticketTitle')}
            </Typography>
          </Box>
          <Box className="ticket-list-header-right">
            {!hideHeader && (
              <Box className="ticket-ac-action-header-wrapper">
                <Typography variant="caption" className="ticket-header-label">
                  {t('agent.tickets.actions')}
                </Typography>
              </Box>
            )}
            <Box className="ticket-type-chip-wrapper">
              <Typography variant="caption" className="ticket-header-label">
                {t('agent.tickets.type')}
              </Typography>
            </Box>
            <Box className="ticket-priority-chip-wrapper">
              <Typography variant="caption" className="ticket-header-label">
                {t('agent.tickets.priority')}
              </Typography>
            </Box>
          </Box>
        </Box>
        {tickets.map((ticket) => renderTicketItem(ticket, 0))}
      </Paper>
    </Box>
  );
};

export default TicketList;